<script setup>
  import { ref, computed, onMounted, nextTick, provide, watch, watchEffect, useTemplateRef } from "vue";
  import { useResizeObserver } from '@vueuse/core';

  const emit = defineEmits(['segmentChanged']);

  const props = defineProps({
    label: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    defaultValue: {
      type: [String, Number, Boolean],
      required: false
    },
    direction: {
      type: String,
      default: 'row'
    },
    size: {
      type: String,
      required: false
    },    
    shape: {
      type: String,
      default: 'rounded'
    },
    iconOnly: {
      type: Boolean,
      default: false
    }
  });

  const model = defineModel();
  const previous = defineModel('previous');

  let activeItemWidth = ref(0);
  let activePosition = ref(0);
  let isUserInput = ref(false);
  let buttonRefs = ref([]);
  // ref() of a ref is that same ref, so this is the model itself: a button setting it updates the
  // parent's v-model
  let activeValue = ref(model);
  let updateIndicator = ref(false);
  let disabled = ref(props.disabled);

  if (!activeValue.value && props.defaultValue) activeValue.value = props.defaultValue;

  // Provided to segment buttons
  provide('buttonRefs', buttonRefs);
  provide('activeValue', activeValue);
  provide('isUserInput', isUserInput);
  provide('updateIndicator', updateIndicator);
  provide('disabled', disabled);
  provide('changeHandler', changeHandler);

  const indicatorStyles = computed(() => ({
      '--activeIndicatorWidth': `${activeItemWidth.value}px`,
      '--activeIndicatorPosition': `${activePosition.value}px`,
    })
  );

  const controlClasses = computed(() => ({
      'animate-indicator': isUserInput.value,
      'jwx-segmented-control': true,
      'jwx-segmented-control--disabled': props.disabled,
    })
  );

  function changeHandler(e) {
    emit('segmentChanged', {
      value: e.target.value,
      previous: previous.value,
    });
    
  }

  async function updateControlIndicator() {
    await nextTick();

    buttonRefs.value.map(ref => {
      if (ref.dataset.active === 'false') return;

      activeItemWidth.value = ref.parentElement.offsetWidth;
      activePosition.value = ref.parentElement.offsetLeft;
    })

    updateIndicator.value = false;
  }

  const list = useTemplateRef('list');

  onMounted(() => updateControlIndicator());

  // The indicator is sized and placed from the active button, so re-measure whenever the
  // control's size changes (a layout that stretches it, a window resize), not just on mount
  useResizeObserver(list, () => updateControlIndicator());

  // No write back to the model here: activeValue is the model, and writing a value the parent just
  // set made Vue emit it again (a reset could become the parent's own setting)
  watch(activeValue, (newValue, oldValue) => {
    previous.value = oldValue;
    updateControlIndicator();
  })

  watch(updateIndicator, updateControlIndicator);
  watchEffect(() => disabled.value = props.disabled);
</script>

<template>
  <ul 
    role=menu
    :class="controlClasses" 
    :style="indicatorStyles"
    :aria-label="props.label"
    :data-shape="props.shape"
    :data-icon-only="props.iconOnly"
    :data-size="props.size"
    ref="list"
  >
    <slot></slot>
  </ul>
</template>